import { Component, type ErrorInfo, type ReactNode } from 'react';
import { UnpluggedArt } from '@/illustrations';
import { isChunkLoadError, reloadForNewBuild } from '@/lib/staleBuild';
import { componentOf, reportClientError } from '@/lib/clientErrors';

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // A lazy screen from a build that's no longer deployed: reload into the
    // new one (at most once per 30 s) rather than show "that broke".
    if (isChunkLoadError(error) && reloadForNewBuild()) return;
    console.error('[popin] render error', error, info.componentStack);
    reportClientError('screen_error', componentOf(info.componentStack), error);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-ground px-6 text-center" role="alert">
        <UnpluggedArt className="mb-6 h-24 w-48" />
        <h1 className="font-display text-[44px] leading-[0.92]">Well, that broke.</h1>
        <p className="mt-3 max-w-md text-muted">Something on this screen crashed. It’s on us, not you. Reloading usually sorts it out.</p>
        <div className="mt-8 flex gap-3">
          <button type="button" className="h-12 rounded-control bg-primary px-6 font-semibold text-ground" onClick={() => window.location.reload()}>
            Reload
          </button>
          <a href="/home" className="flex h-12 items-center rounded-full bg-raised px-6 font-semibold">
            Go home
          </a>
        </div>
      </div>
    );
  }
}
